import { Component, Injector, Input, OnInit } from '@angular/core';
import { Observable, first } from 'rxjs';
import { WidgetDynamicBaseComponent } from '../../widgetDynamicBase';

/**
 * Direct Cap Lease Summary Widget
 */
@Component({
  selector: 'lease-summary-widget',
  templateUrl: './lease-summary-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './lease-summary-widget.component.scss']
})
export class LeaseSummaryWidgetComponent extends WidgetDynamicBaseComponent implements OnInit{

  public rows: any[] = [];
  public rows2: any[] = [];
  

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'leaseSummary', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {    
    this.useDefaultServiceSaveChangesHandler("leaseSummary", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.currentLayout || this.currentLayout === "default" || this.currentLayout === "ChosenOnAdd") {
      this.chooseNextLayout();
    }

    this.mockupData$ = this.widgetService.loadMockup('leaseSummary');

    this.widgetData$ = this.mockup ?
      this.mockupData$ : this.loadLayout(this.currentLayout);
  }


  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    this.rows = [
      { label: 'labelLessor', data: 'lessor' },
      { label: 'labelLessee', data: 'lessee' },
      { label: 'labelCreditRating', data: 'creditRating', additionalData: 'creditRatingSource' },
      { label: 'labelLeasedArea', data: 'leasedArea', additionalData: 'unitOfArea', type: 'number' },
      { label: 'labelExpenseStructure', data: 'expenseStructure' },
      { label: 'labelOwnerExpenseResponsibilities', data: 'ownerExpenseResponsibilities' },
      { label: 'labelTenantExpenseResponsibilities', data: 'tenantExpenseResponsibilities' },
      { label: 'labelLeaseCommencement', data: 'leaseCommencement', type: 'date' },
      { label: 'labelExpiration', data: 'expiration', type: 'date' },
      { label: 'labelLeaseTerm', data: 'leaseTerm' },
      { label: 'labelRemainingLeaseTerm', data: 'remainingLeaseTerm' }
    ];

    this.rows2 = [
      { label: 'labelPercentageRent', data: 'usLeaseSummaryNPercentageRent' },
      { label: 'labelCancellationClause', data: 'usLeaseSummaryNCancellationClause' },
      { label: 'labelRightOfFirstRefusalClause', data: 'usLeaseSummaryNRightofFirstRefusalClause' },
      { label: 'labelBuyBackClause', data: 'usLeaseSummaryNBuyBackClause' },
      { label: 'labelCoTenancyClause', data: 'usLeaseSummaryNCoTenancyClause' },
      { label: 'labelCasualtyClause', data: 'usLeaseSummaryNCasualtyClause' },
      { label: 'labelCondemnationClause', data: 'usLeaseSummaryNCondemnationClause' },
      { label: 'labelLeaseNotes', data: 'usLeaseSummaryNLeaseNotes' },
      { label: 'LabelSource', data: 'usLeaseSummaryNSource' }
    ];
   
    this.rows = this.rows.filter((row) => {
      return data[row.data];
    });

    this.rows2 = this.rows2.filter((row) => {
      return data[row.data];
    });
    return data;
  }
  
  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    this.widgetConfig.widgetSettings.currentLayout = newLayout;

    if (this.widgetData.reportId) {
      this.loadLayout(newLayout).pipe(first()).subscribe(data => {
        this.widgetData = this.preprocessLoadedData(data);
      });
    }
  }

  /**
   * Load widget data for given layoutId
   * @param layout {number} Layout Id
   */
  loadLayout(layoutId: string): Observable<any> {
    return this.widgetService.loadFromWidgetResource(`leaseSummary/layout?id=${this.getReport()}&layoutId=${layoutId}`);
  }

  /**
   * Choose next available layout when adding widget into report document
   */
  private chooseNextLayout() {
    const activeDocument = this.rdStorage.getActiveDocument();
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId === this.widgetConfig.widgetId) {
          usedLayouts.push(widget.widgetSettings.currentLayout);
        }
      }
    }

    for (const layout of this.widgetConfig.widgetSettings.availableLayouts) {
      if (usedLayouts.indexOf(layout) < 0) {
        this.currentLayout = layout;
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
        break;
      }
    }

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    });
  }

  isVisible(index: number): boolean {
    let visibleIndex = 0;
    for (let i = 0; i <= index; i++) {
      if (this.widgetData[this.rows[i].data] !== null) {
        visibleIndex++;
      }
    }
    return visibleIndex % 2 === 1;
  }

  public getMinimumRowIndex(): number {
    return 1;
  }

  public getMaximumRowIndex(): number {
    if (this.widgetData) {
      return this.widgetData.rows?.length + this.rows2.length + this.rows?.length + 6;
    }
    return -1;
  }

  public moreColumnsRequestedIfOnPortraitPage(): boolean {
    return false;
  }
}

